import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Location } from '@angular/common';
import { computed, inject, Signal } from '@angular/core'; // Added Signal import
import { Router } from '@angular/router';
import { ActionType } from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { createCustomStateSetOnUrlHandler } from '../../helpers';
import { addEntity, deleteEntity, getAll, getOne, StateDictionary, updateEntity } from '../../helpers/store-base';
import { ActionTypesService } from './action-types.service';

type ActionTypesState = {
  actionTypes: ActionType[];
  isLoading: boolean;
  actionTypeCreateLoading: boolean;
  selectedActionType: ActionType | null;
  selectedActionTypeLoading: boolean;
};

const initialState: ActionTypesState = {
  actionTypes: [],
  isLoading: false,
  actionTypeCreateLoading: false,
  selectedActionType: null,
  selectedActionTypeLoading: false,
};

const actionTypesDictionary: StateDictionary<ActionType> = {
  entityList: 'actionTypes',
  listLoading: 'isLoading',
  selectedEntity: 'selectedActionType',
  selectedEntityLoading: 'selectedActionTypeLoading',
  entityId: 'code',
  createLoading: 'actionTypeCreateLoading',
  sortFn: (a, b) => a.code.localeCompare(b.code),
};

export const ActionTypesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('action-types-store'),

  withComputed(store => ({
    selectedActionTypeId: computed(() => store.selectedActionType()?.code || null),
  })),

  withMethods((store, actionTypesService = inject(ActionTypesService), router = inject(Router), location = inject(Location)) => {
    createCustomStateSetOnUrlHandler<ActionTypesState>('/actions/types', { selectedActionType: null })(store);

    const repositoryMethods = {
      getAll: getAll<ActionType>({
        store,
        service: actionTypesService,
        dictionary: actionTypesDictionary,
      }),

      getOne: getOne<ActionType>({
        store,
        service: actionTypesService,
        dictionary: actionTypesDictionary,
      }),

      addActionType: addEntity<ActionType, ActionType>({
        store,
        service: actionTypesService,
        dictionary: actionTypesDictionary,
        onSuccessCallback: (actionType: ActionType) => {
          router.navigate(['actions', 'types', actionType.code, 'details']);
          patchState(store, { selectedActionType: actionType });
        },
      }),

      updateActionType: updateEntity<ActionType, ActionType>({
        store,
        service: actionTypesService,
        dictionary: actionTypesDictionary,
        onSuccessCallback: (actionType: ActionType) => {
          location.go(`actions/types/${actionType.code}/details`);
          patchState(store, { selectedActionType: actionType });
        },
      }),
    };

    const selectActionType = (code: string) => {
      const selected = store.actionTypes().find(at => at.code === code) || null;
      patchState(store, { selectedActionType: selected });
      if (!selected) repositoryMethods.getOne(code);
    };

    return {
      ...repositoryMethods,
      selectActionType,

      goToActionTypeActions: async (code: string): Promise<boolean> => {
        const result = await router.navigate(['actions', 'types', code]);
        if (!result) return false;
        selectActionType(code);

        return true;
      },

      deleteActionType: deleteEntity<ActionType>({
        store,
        service: actionTypesService,
        dictionary: actionTypesDictionary,
        onSuccessCallback: () => {
          router.navigate(['actions', 'types']);
          repositoryMethods.getAll();
        },
      }),

      resetState: (state: Partial<ActionTypesState>) => patchState(store, state),
    };
  }),
);

export interface ActionTypesStoreType {
  readonly actionTypes: Signal<ActionType[]>;
  readonly isLoading: Signal<boolean>;
  readonly actionTypeCreateLoading: Signal<boolean>;
  readonly selectedActionType: Signal<ActionType | null>;
  readonly selectedActionTypeLoading: Signal<boolean>;

  readonly selectedActionTypeId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly addActionType: (dto: ActionType) => void;
  readonly updateActionType: (input: { id: string; dto: ActionType }) => void;
  readonly selectActionType: (code: string) => void;
  readonly goToActionTypeActions: (code: string) => Promise<boolean>;
  readonly deleteActionType: (id: string) => void;
  readonly resetState: (state: Partial<ActionTypesState>) => void;
}
